import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CustomerLayout from '../../components/customer/CustomerLayout';
import CreditBreakdown, {
  creditKindLabel,
  formatCreditDateTime
} from '../../components/customer/CreditBreakdown';
import customerApiService from '../../services/customerApi';
import {
  AccountEmployee,
  CreditBucket,
  CreditEntry,
  CreditKind,
  CreditPlanFields,
  CreditsHistoryQuery,
  CreditsSortField,
  CreditsSortOrder
} from '../../types/clientAccount';

const formatCredits = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '0.00';
  const num = Number(value);
  return Number.isNaN(num) ? String(value) : num.toFixed(2);
};

// Los seis kinds del ledger, en el orden en que se ofrecen como filtro.
const KIND_VALUES: CreditKind[] = [
  'topup',
  'consumption',
  'adjustment',
  'plan_grant',
  'plan_expiry',
  'storage'
];

// Presets de ventana temporal del selector: los tres del portal («Todo / Este
// ciclo / Últimos 30 días») más «Rango personalizado», que despliega dos fechas.
type PeriodMode = 'all' | 'cycle' | '30d' | 'custom';

// Página propia del HISTORIAL de créditos: la tabla larga del ledger que antes
// vivía dentro de «Mi cuenta». Aquí se pagina en servidor (muchos registros) y
// se filtra/ordena de verdad. El desglose de bolsas (plan/extra/total) viaja en
// la misma respuesta y se pinta arriba para no perder contexto.
const CustomerCreditsHistoryPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Paginación (server-side): página 0-based y filas por página.
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filtros.
  const [kinds, setKinds] = useState<CreditKind[]>([]);
  const [bucket, setBucket] = useState<CreditBucket | ''>('');
  const [periodMode, setPeriodMode] = useState<PeriodMode>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [employeeId, setEmployeeId] = useState<number | ''>('');

  // Orden.
  const [sort, setSort] = useState<CreditsSortField>('date');
  const [order, setOrder] = useState<CreditsSortOrder>('desc');

  // Datos.
  const [items, setItems] = useState<CreditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [planData, setPlanData] = useState<CreditPlanFields | null>(null);
  const [balance, setBalance] = useState<number | string>(0);
  const [employees, setEmployees] = useState<AccountEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Empleados: para el filtro por empleado y para resolver el nombre en la
  // tabla. Best-effort: si falla, el filtro se queda vacío y los asientos con
  // empleado se pintan como «#id» — la tabla nunca se rompe por esto.
  useEffect(() => {
    customerApiService
      .getMyAccountEmployees()
      .then(setEmployees)
      .catch((err) => console.error('Failed to load employees for filter:', err));
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const query: CreditsHistoryQuery = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort,
        order
      };
      if (kinds.length > 0) query.kind = kinds;
      if (bucket) query.bucket = bucket;
      if (employeeId !== '') query.employee_id = employeeId;
      if (periodMode === 'custom') {
        if (dateFrom) query.date_from = dateFrom;
        if (dateTo) query.date_to = dateTo;
      } else if (periodMode !== 'all') {
        query.period = periodMode;
      }
      const data = await customerApiService.getMyAccountCreditsHistory(query);
      setItems(data.items);
      setTotal(data.total);
      setBalance(data.balance);
      // El desglose viaja en credit_summary; sin él (backend viejo) el
      // componente cae al saldo simple.
      setPlanData(data.credit_summary ?? null);
      setError(null);
    } catch (err) {
      console.error('Failed to load credits history:', err);
      setError(
        t('customer.creditsHistory.loadError', 'No se pudo cargar el historial. Inténtalo de nuevo en unos minutos.')
      );
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, kinds, bucket, employeeId, periodMode, dateFrom, dateTo, sort, order, t]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Cualquier cambio de filtro vuelve a la primera página: paginar sobre un
  // total que acaba de cambiar dejaría al usuario en una página vacía.
  const handleKindsChange = (event: SelectChangeEvent<CreditKind[]>) => {
    const value = event.target.value;
    setKinds(typeof value === 'string' ? (value.split(',') as CreditKind[]) : value);
    setPage(0);
  };
  const handleBucketChange = (event: SelectChangeEvent) => {
    setBucket(event.target.value as CreditBucket | '');
    setPage(0);
  };
  const handlePeriodChange = (event: SelectChangeEvent) => {
    setPeriodMode(event.target.value as PeriodMode);
    setPage(0);
  };
  const handleEmployeeChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setEmployeeId(value === '' ? '' : Number(value));
    setPage(0);
  };

  const handleSort = (field: CreditsSortField) => {
    if (sort === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(field);
      setOrder('desc');
    }
    setPage(0);
  };

  const clearFilters = () => {
    setKinds([]);
    setBucket('');
    setPeriodMode('all');
    setDateFrom('');
    setDateTo('');
    setEmployeeId('');
    setPage(0);
  };

  const hasFilters =
    kinds.length > 0 ||
    bucket !== '' ||
    periodMode !== 'all' ||
    employeeId !== '' ||
    dateFrom !== '' ||
    dateTo !== '';

  const employeeName = (id: number | null): string => {
    if (id === null) return '—';
    const emp = employees.find((e) => e.id === id);
    return emp ? emp.display_name || emp.listing_slug : `#${id}`;
  };

  return (
    <CustomerLayout>
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/customer/account')}
          sx={{ textTransform: 'none', mb: 2 }}
        >
          {t('customer.creditsHistory.back', 'Volver a Mi cuenta')}
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          {t('customer.creditsHistory.title', 'Historial de créditos')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t(
            'customer.creditsHistory.subtitle',
            'Todos los movimientos de tu saldo: recargas, consumo, plan y almacenamiento.'
          )}
        </Typography>

        {/* Desglose de bolsas: mismo componente que «Mi cuenta», para no perder
            el contexto de cuánto plan/extra/total hay al mirar el detalle. */}
        <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mb: 3 }}>
          <CardContent>
            <CreditBreakdown data={planData} balance={balance} />
          </CardContent>
        </Card>

        {/* Filtros */}
        <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
              <FormControl size="small" sx={{ minWidth: 200, flex: '1 1 200px' }}>
                <InputLabel>{t('customer.creditsHistory.filterKind', 'Tipo')}</InputLabel>
                <Select
                  multiple
                  value={kinds}
                  onChange={handleKindsChange}
                  input={<OutlinedInput label={t('customer.creditsHistory.filterKind', 'Tipo')} />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} size="small" label={creditKindLabel(value, t)} />
                      ))}
                    </Box>
                  )}
                >
                  {KIND_VALUES.map((value) => (
                    <MenuItem key={value} value={value}>
                      {creditKindLabel(value, t)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150, flex: '1 1 150px' }}>
                <InputLabel>{t('customer.creditsHistory.filterBucket', 'Bolsa')}</InputLabel>
                <Select
                  value={bucket}
                  onChange={handleBucketChange}
                  label={t('customer.creditsHistory.filterBucket', 'Bolsa')}
                >
                  <MenuItem value="">{t('customer.creditsHistory.bucketAll', 'Todas')}</MenuItem>
                  <MenuItem value="plan">{t('customer.creditsHistory.bucketPlan', 'Plan')}</MenuItem>
                  <MenuItem value="extra">{t('customer.creditsHistory.bucketExtra', 'Extra')}</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 170, flex: '1 1 170px' }}>
                <InputLabel>{t('customer.creditsHistory.filterPeriod', 'Periodo')}</InputLabel>
                <Select
                  value={periodMode}
                  onChange={handlePeriodChange}
                  label={t('customer.creditsHistory.filterPeriod', 'Periodo')}
                >
                  <MenuItem value="all">{t('customer.account.period.all', 'Todo')}</MenuItem>
                  <MenuItem value="cycle">{t('customer.account.period.cycle', 'Este ciclo')}</MenuItem>
                  <MenuItem value="30d">{t('customer.account.period.last30d', 'Últimos 30 días')}</MenuItem>
                  <MenuItem value="custom">{t('customer.creditsHistory.periodCustom', 'Rango de fechas')}</MenuItem>
                </Select>
              </FormControl>

              {periodMode === 'custom' && (
                <>
                  <TextField
                    size="small"
                    type="date"
                    label={t('customer.creditsHistory.dateFrom', 'Desde')}
                    InputLabelProps={{ shrink: true }}
                    value={dateFrom}
                    onChange={(e) => {
                      setDateFrom(e.target.value);
                      setPage(0);
                    }}
                    sx={{ flex: '1 1 150px' }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label={t('customer.creditsHistory.dateTo', 'Hasta')}
                    InputLabelProps={{ shrink: true }}
                    value={dateTo}
                    onChange={(e) => {
                      setDateTo(e.target.value);
                      setPage(0);
                    }}
                    sx={{ flex: '1 1 150px' }}
                  />
                </>
              )}

              <FormControl size="small" sx={{ minWidth: 180, flex: '1 1 180px' }}>
                <InputLabel>{t('customer.creditsHistory.filterEmployee', 'Empleado')}</InputLabel>
                <Select
                  value={employeeId === '' ? '' : String(employeeId)}
                  onChange={handleEmployeeChange}
                  label={t('customer.creditsHistory.filterEmployee', 'Empleado')}
                >
                  <MenuItem value="">{t('customer.creditsHistory.all', 'Todos')}</MenuItem>
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={String(emp.id)}>
                      {emp.display_name || emp.listing_slug}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {hasFilters && (
                <Button
                  onClick={clearFilters}
                  sx={{ textTransform: 'none', alignSelf: 'center' }}
                >
                  {t('customer.creditsHistory.clearFilters', 'Limpiar filtros')}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Tabla del ledger, paginada y ordenable. */}
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={sort === 'date' ? order : false}>
                  <TableSortLabel
                    active={sort === 'date'}
                    direction={sort === 'date' ? order : 'desc'}
                    onClick={() => handleSort('date')}
                  >
                    {t('customer.account.fields.date', 'Fecha')}
                  </TableSortLabel>
                </TableCell>
                <TableCell>{t('customer.account.fields.kind', 'Tipo')}</TableCell>
                <TableCell>{t('customer.creditsHistory.filterBucket', 'Bolsa')}</TableCell>
                <TableCell>{t('customer.account.fields.concept', 'Concepto')}</TableCell>
                <TableCell>{t('customer.account.fields.employee', 'Empleado')}</TableCell>
                <TableCell align="right" sortDirection={sort === 'amount' ? order : false}>
                  <TableSortLabel
                    active={sort === 'amount'}
                    direction={sort === 'amount' ? order : 'desc'}
                    onClick={() => handleSort('amount')}
                  >
                    {t('customer.account.fields.credits', 'Créditos')}
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasFilters
                        ? t('customer.creditsHistory.noResults', 'No hay movimientos con estos filtros.')
                        : t('customer.account.noCreditEntries', 'Sin movimientos todavía.')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((entry) => {
                  const amount = Number(entry.amount);
                  return (
                    <TableRow key={entry.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatCreditDateTime(entry.created_at)}
                      </TableCell>
                      <TableCell>
                        <Chip label={creditKindLabel(entry.kind, t)} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {entry.bucket === 'plan'
                          ? t('customer.creditsHistory.bucketPlan', 'Plan')
                          : t('customer.creditsHistory.bucketExtra', 'Extra')}
                      </TableCell>
                      <TableCell>{entry.concept || '—'}</TableCell>
                      <TableCell>{employeeName(entry.account_employee_id)}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={amount < 0 ? 'error.main' : 'success.main'}
                        >
                          {amount > 0 ? '+' : ''}
                          {formatCredits(entry.amount)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage={t('customer.creditsHistory.rowsPerPage', 'Filas por página')}
          />
        </TableContainer>
      </Container>
    </CustomerLayout>
  );
};

export default CustomerCreditsHistoryPage;
